/**
 * "Already on MPS?" check before a supplier is created (2026-10-20).
 *
 * POST /supplier/existingSupplierMatches answers likely existing MPS suppliers for the e-mail and company name that
 * were typed. The guarded create paths (/supplier/addBuyerMySupplier, /supplier/add, /supplier/addSupplierByBuyer,
 * /services/rfq/addSupplierByBuyerForServiceRfq, /rfq/addSupplierByBuyerFromImportedRfq) answer HTTP 409
 * EXISTING_SUPPLIER_MATCHES with the same list when no explicit choice was sent. A match carries only id, company,
 * country and why it matched - never the other company's e-mails or phones.
 */
export type ExistingSupplierMatchReason = 'email' | 'domain' | 'name';

export interface ExistingSupplierMatch {
  supplierId: number;
  companyName: string;
  country: string | null;
  matchedOn: ExistingSupplierMatchReason[];
  /** Already in this buyer's My Suppliers. */
  alreadyLinked: boolean;
}

/** What a create call is sent after the choice: link that supplier, or create a new one anyway. */
export interface SupplierCreateChoice {
  linkSupplierId?: number;
  createNew?: boolean;
  /** Display only (not sent): the linked company's name. */
  linkedName?: string;
}

export const EXISTING_SUPPLIER_MATCHES_CODE = 'EXISTING_SUPPLIER_MATCHES';

/** The matches a guarded create path answered with (409), or null for any other error. */
export function existingMatchesFromError(error: any): ExistingSupplierMatch[] | null {
  const body = error?.error;
  if (error?.status !== 409 || body?.code !== EXISTING_SUPPLIER_MATCHES_CODE) return null;
  return Array.isArray(body.matches) ? body.matches : [];
}

/** The typed e-mail already belongs to a supplier (login or contact): Link is offered, "create new" is not. */
export function hasExactEmailMatch(matches: ExistingSupplierMatch[] | null | undefined): boolean {
  return (Array.isArray(matches) ? matches : []).some((m) => (m.matchedOn || []).includes('email'));
}

/**
 * Matches the buyer chooses between. Every match, an exact e-mail one included, is offered as Link (2026-10-20 M1:
 * both create paths behave the same).
 */
export function matchesNeedingChoice(matches: ExistingSupplierMatch[] | null | undefined): ExistingSupplierMatch[] {
  return Array.isArray(matches) ? matches : [];
}

const REASON_LABEL: Record<ExistingSupplierMatchReason, string> = {
  email: 'same e-mail',
  domain: 'same company e-mail domain',
  name: 'same company name',
};

/** "same company e-mail domain, same company name" */
export function matchReasonLabel(match: ExistingSupplierMatch): string {
  return (match?.matchedOn || []).map((r) => REASON_LABEL[r] || r).join(', ');
}
